'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { projectsApi, contentApi } from '@/lib/api';

interface Project { id: string; name: string; slug: string; }

interface ContentPost {
  id: string; project_id: string; platform: string; hook: string | null;
  caption: string | null; topic: string | null; script: string | null;
  status: string; content_type: string | null; created_at: number | null;
}

interface ContentContextType {
  projects: Project[];
  selectedProjectId: string;
  selectedProjectSlug: string;
  setSelectedProject: (id: string, slug: string) => void;
  posts: ContentPost[];
  postsLoading: boolean;
  refreshPosts: () => Promise<void>;
}

const ContentContext = createContext<ContentContextType | null>(null);

export function ContentProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('beast:content:projectId') || '';
    return '';
  });
  const [selectedProjectSlug, setSelectedProjectSlug] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('beast:content:projectSlug') || '';
    return '';
  });
  const [posts, setPosts] = useState<ContentPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);

  useEffect(() => {
    projectsApi.list()
      .then((data: any) => {
        const list = (data.projects || data || []).map((p: Project) => ({ id: p.id, name: p.name, slug: p.slug }));
        setProjects(list);
        if (!selectedProjectId && list.length > 0) {
          setSelectedProjectId(list[0].id);
          setSelectedProjectSlug(list[0].slug);
          localStorage.setItem('beast:content:projectId', list[0].id);
          localStorage.setItem('beast:content:projectSlug', list[0].slug);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSelectedProject = useCallback((id: string, slug: string) => {
    setSelectedProjectId(id);
    setSelectedProjectSlug(slug);
    localStorage.setItem('beast:content:projectId', id);
    localStorage.setItem('beast:content:projectSlug', slug);
  }, []);

  const refreshPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      // No project selected = "All Projects" — fetch everything
      const data = selectedProjectId
        ? await contentApi.posts({ projectId: selectedProjectId })
        : await contentApi.postsAll();
      setPosts(Array.isArray(data) ? data : []);
    } finally { setPostsLoading(false); }
  }, [selectedProjectId]);

  useEffect(() => { refreshPosts(); }, [selectedProjectId, refreshPosts]);

  return (
    <ContentContext.Provider value={{
      projects, selectedProjectId, selectedProjectSlug, setSelectedProject,
      posts, postsLoading, refreshPosts,
    }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
}
